<script setup lang="ts">
import { GpBtn, GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import AddSshKeysModal from '@panel/components/user/ssh-keys/AddSshKeysModal.vue';
import useGpTableData from '@panel/composables/useGpTableData';
import useUserViewTitle from '@panel/composables/useUserViewTitle';
import UserSettingsLayout from '@panel/layouts/Layout.User.Settings.vue';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import getEnumLabel from 'gpcore-apikit/lib/getEnumLabel';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { SSHKeyType } from 'gpcore-grpc/gpcore/type/v1/ssh_key_pb';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, onMounted, reactive, ref } from 'vue';

useUserViewTitle('SSH public keys');

interface SSHKeysTableRow extends GpTableDataRow {
  name: string;
  type: string;
  fingerprint: string;
  selectable: true;
  key: string;
}

const destroyModal = reactive({ show: false });
const createModal = reactive({ show: false });

const userStore = useUserStore();
const keys = computed(() => userStore.sshKeys);
const { tableData, selectedRows: toDelete } = useGpTableData(keys, (data) =>
  data.map((key) => ({
    key: key.id,
    selectable: true,
    name: key.name,
    type: getEnumLabel(SSHKeyType, key.type),
    fingerprint: key.fingerprint !== undefined ? key.fingerprint : '-',
  })),
);

const doDelete = (row?: SSHKeysTableRow) => {
  if (row) toDelete.value = [row];
  if (toDelete.value.length === 0) return;

  destroyModal.show = true;
};
const isDeleting = ref(false);
const { addNotification } = useNotifications();

const onDelete = async () => {
  if (!destroyModal.show) {
    return;
  }

  isDeleting.value = true;
  const promises = toDelete.value.map((row) => userStore.deleteSSHKey(row.key));

  try {
    await Promise.all(promises);
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isDeleting.value = false;
  }

  destroyModal.show = false;
};

onMounted(() => {
  userStore.reloadSSHKeys().catch((error: ServiceError) => {
    addNotification({ message: error.message, type: 'danger' });
  });
});
</script>

<template>
  <UserSettingsLayout v-playwright="'ssh-keys-view'">
    <template #buttons>
      <GpBtn
        btn-type="danger"
        size="sm"
        outline
        :disabled="toDelete.length === 0"
        label="Remove"
        v-playwright="`delete-ssh-key-btn`"
        :icon="['fas', 'fa-trash']"
        @click="doDelete()"
      />

      <GpBtn
        size="sm"
        :icon="['fas', 'fa-plus']"
        label="Add"
        v-playwright="`add-ssh-key-btn`"
        @click="createModal.show = true"
      />
    </template>
    <template #content>
      <ConfirmModal
        :show="destroyModal.show"
        confirm-text="Remove"
        confirm-type="danger"
        :loading="isDeleting"
        @close="destroyModal.show = false"
        @confirm="onDelete"
      />
      <AddSshKeysModal
        :show="createModal.show"
        @close="createModal.show = false"
        @created="createModal.show = false"
      />

      <GpTable
        :data="tableData"
        v-model:selected-rows="toDelete"
        empty-message="No SSH public keys added jet."
        v-playwright="'ssh-keys-table'"
      >
        <GpTableColumn field="checkbox" selection="checkbox" />
        <GpTableColumn field="name" label="Name" />
        <GpTableColumn field="type" label="Type" />
        <GpTableColumn field="fingerprint" label="Fingerprint" />
        <GpTableColumn field="actions" column-class="tw-text-end">
          <template #body="{ row }: { row: SSHKeysTableRow }">
            <GpBtn
              size="sm"
              btn-type="danger"
              :icon="['fas', 'fa-trash']"
              label="Remove"
              @click="doDelete(row)"
            />
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </UserSettingsLayout>
</template>
